import React, { useState } from "react";
import { AlertTriangle, Clock, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Calcula días restantes a partir de una fecha ISO de vencimiento.
function daysLeftFrom(expiresAt) {
  if (!expiresAt) return null;
  const exp = new Date(expiresAt).getTime();
  if (Number.isNaN(exp)) return null;
  return Math.ceil((exp - Date.now()) / 86400000);
}

// Barra discreta superior de recordatorio de vencimiento (solo admin del colegio).
export function RenewBar() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (!user || user.role === "staff" || user.role === "owner") return null;
  const dl = daysLeftFrom(user.expires_at);
  if (dl === null || dl > 30 || dismissed) return null;

  // Niveles de urgencia: >5 días (sutil), 1–5 (ámbar), <=0 (rojo, último día).
  let tone, icon, label;
  if (dl <= 0) {
    tone = "bg-red-600 text-white border-red-700";
    icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    label = dl === 0 ? "Tu suscripción vence HOY." : "Tu suscripción está vencida.";
  } else if (dl <= 5) {
    tone = "bg-amber-500 text-white border-amber-600";
    icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    label = `Tu suscripción vence en ${dl} día${dl === 1 ? "" : "s"}.`;
  } else {
    tone = "bg-navy/5 text-navy border-navy/15";
    icon = <Clock className="w-4 h-4 shrink-0" />;
    label = `Tu suscripción vence en ${dl} días.`;
  }

  return (
    <div className={`w-full border-b ${tone}`} data-testid="renew-bar">
      <div className="max-w-full px-4 sm:px-6 lg:px-10 py-2 flex items-center gap-3 text-sm">
        {icon}
        <span className="font-medium truncate" data-testid="renew-bar-label">{label}</span>
        <span className="hidden sm:inline opacity-80">Contacta a tu proveedor para renovar.</span>
        <div className="ml-auto flex items-center gap-2">
          {dl > 5 && (
            <button onClick={() => setDismissed(true)} data-testid="renew-bar-dismiss" className="opacity-60 hover:opacity-100" aria-label="Ocultar">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Pantalla completa cuando la licencia ya venció (bloquea el acceso hasta renovar).
export function RenewalGate({ billing }) {
  const { logout } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-ink px-4" data-testid="renewal-gate">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl ring-1 ring-black/5 p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <h1 className="font-display font-bold text-2xl text-navy mb-1">Suscripción vencida</h1>
        <p className="text-sm text-nardo-600 mb-1">{billing?.school_name || "Tu colegio"}</p>
        <p className="text-sm text-nardo-500 mb-6">
          Tu suscripción venció. Contacta a tu proveedor para renovar tu acceso a la plataforma.
        </p>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-4">
          Comunícate con tu proveedor para reactivar tu cuenta.
        </div>
        <button onClick={logout} data-testid="gate-logout" className="mt-5 text-xs uppercase tracking-[0.25em] text-nardo-500 hover:text-navy">
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
